import { ValueFormatterParams } from '@ag-grid-community/core';
import LocaleCurrency from 'locale-currency';

import { BillingPeriodMapper } from 'src/services/api/productsApi';

interface CurrencyFormatterProps {
  value: number;
  defaultVal?: any;
  currency?: string;
  formatOptions?: Intl.NumberFormatOptions;
}
interface TimestampFormatterProps {
  value: number | string;
}

let currencyFormatter = new Intl.NumberFormat();

export const GetDefaultCurrency = () =>
  LocaleCurrency.getCurrency(navigator.language) || 'USD';

export function GetCurrencyFormatter(
  currency: string,
  options?: Intl.NumberFormatOptions,
) {
  const opts = options || {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  const currentOptions = currencyFormatter.resolvedOptions();

  // return existing formatter if options are the same
  // 1. currency matches
  // 2. maximum fraction digits matches
  //   a. if maximum fraction digits is not set, we can use the existing formatter
  //   b. if maximum fraction digits is set, we check if it matches the existing formatter
  if (
    currentOptions.currency === currency &&
    (!opts.maximumFractionDigits ||
      currentOptions.maximumFractionDigits === opts.maximumFractionDigits)
  ) {
    return currencyFormatter;
  }

  currencyFormatter = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency,
    ...opts,
  });
  return currencyFormatter;
}

export const CentsCurrencyFormatter = ({
  value,
  defaultVal = 0,
  currency,
  formatOptions,
}: CurrencyFormatterProps) => {
  const formatCurrency = currency || GetDefaultCurrency();
  const formatter = GetCurrencyFormatter(formatCurrency, formatOptions);

  if (!value) {
    return formatter.format(defaultVal);
  }
  return formatter.format(value);
};

export const StripeCurrencyFormatterAdapter = ({
  value,
  currency,
}: CurrencyFormatterProps) => {
  let valueToFormat = value;
  // ensure value is a number otherwise toFixed operations will not work
  // it can also be string when being passed from getQuickFilterText
  if (typeof valueToFormat !== 'number') {
    valueToFormat = Number(valueToFormat);
  }

  // this is required to round up the value https://linear.app/portaltechnologies/issue/POR-6616#comment-c92d029d
  // using toFixed is required to handle floating point errors. E.g. $355.5449999999
  // would get rounded to $355.54 instead of $355.55
  const roundedValue = Number(valueToFormat.toFixed(2));
  return CentsCurrencyFormatter({
    value: roundedValue / 100,
    currency,
    formatOptions: {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    },
  });
};

export const StripeCurrencyTableFormatter = (params: ValueFormatterParams) => {
  const stripeEntity =
    params.data.subscriptionFullData || params.data.invoiceFullData;
  const isCurrencyDefined =
    params.data.invoiceFullData &&
    !params.data.invoiceFullData?.fields?.currency; // Subscription invoices have currency property missing. If so, set USD as default

  return CentsCurrencyFormatter({
    value: params.value / 100,
    currency: stripeEntity?.fields.currency || (isCurrencyDefined && 'USD'),
  });
};

export const GetCurrencyPrefix = ({ currency }: { currency: string }) => {
  const formatter = GetCurrencyFormatter(currency);
  const formatterOptions = formatter.formatToParts();
  const prefix = formatterOptions.find((opt) => opt.type === 'currency')?.value;
  return prefix;
};

export function GetAmountWithCommas(amount: string, currency: string) {
  return Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency,
  });
}

export const MonthDayYearFormatter = (props: TimestampFormatterProps) =>
  props.value
    ? new Date(
        typeof props.value === 'number' ? props.value * 1000 : props.value,
      ).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

export const DownloadCSVNumberFormatter = ({
  value,
  currency,
}: {
  value: number;
  currency: string;
}) => {
  const formatter = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency,
  });
  const formattedNumber = formatter.format(value / 100);
  return formattedNumber;
};

/**
 *
 * @returns price with interval for subscriptions
 * @example: $10.00 / month
 */
export const PriceIntervalFormatter = (params: ValueFormatterParams) => {
  const stripeEntity =
    params.data.subscriptionFullData || params.data.invoiceFullData;
  const formattedCurrency = StripeCurrencyTableFormatter(params);
  return `${formattedCurrency}${
    stripeEntity?.fields.interval
      ? ` / ${BillingPeriodMapper[stripeEntity?.fields.interval]}`
      : ''
  }`;
};

export const MonthDayFormatter = (props: TimestampFormatterProps) =>
  props.value
    ? new Date(
        typeof props.value === 'number' ? props.value * 1000 : props.value,
      ).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })
    : '';
